import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  locationsService,
  manufacturersService,
  usersService,
  dashboardSettingsService,
  locationManufacturerSettingsService,
  notificationSettingsService,
  type CreateLocationInput,
  type UpdateLocationInput,
  type CreateManufacturerInput,
  type UpdateManufacturerInput,
  type CreateUserInput,
  type UpdateUserInput,
  type UpdateUserLocationsInput,
  type UpdateDashboardSettingsInput,
  type CreateLocationManufacturerSettingsInput,
  type UpdateLocationManufacturerSettingsInput,
  type CreateNotificationSettingsInput,
  type UpdateNotificationSettingsInput,
} from '../services';
import { settingsService } from '../../../shared/services/settingsService';

// ============================================================================
// LOCATIONS
// ============================================================================

export function useLocations() {
  return useQuery({
    queryKey: ['admin', 'locations'],
    queryFn: () => locationsService.getLocations(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useLocation(id: string) {
  return useQuery({
    queryKey: ['admin', 'locations', id],
    queryFn: () => locationsService.getLocation(id),
    enabled: !!id,
  });
}

export function useCreateLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateLocationInput) =>
      locationsService.createLocation(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'locations'] });
    },
  });
}

export function useUpdateLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateLocationInput) =>
      locationsService.updateLocation(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'locations'] });
    },
  });
}

export function useDeleteLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => locationsService.deleteLocation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'locations'] });
    },
  });
}

// ============================================================================
// MANUFACTURERS
// ============================================================================

export function useManufacturers() {
  return useQuery({
    queryKey: ['admin', 'manufacturers'],
    queryFn: () => manufacturersService.getManufacturers(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useActiveManufacturers() {
  return useQuery({
    queryKey: ['admin', 'manufacturers', 'active'],
    queryFn: () => manufacturersService.getActiveManufacturers(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useManufacturer(id: string) {
  return useQuery({
    queryKey: ['admin', 'manufacturers', id],
    queryFn: () => manufacturersService.getManufacturer(id),
    enabled: !!id,
  });
}

export function useCreateManufacturer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateManufacturerInput) =>
      manufacturersService.createManufacturer(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'manufacturers'] });
      queryClient.invalidateQueries({ queryKey: ['manufacturers'] }); // Global cache
    },
  });
}

export function useUpdateManufacturer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateManufacturerInput) =>
      manufacturersService.updateManufacturer(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'manufacturers'] });
      queryClient.invalidateQueries({ queryKey: ['manufacturers'] });
    },
  });
}

export function useDeleteManufacturer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => manufacturersService.deleteManufacturer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'manufacturers'] });
      queryClient.invalidateQueries({ queryKey: ['manufacturers'] });
    },
  });
}

// ============================================================================
// USERS
// ============================================================================

export function useUsers() {
  return useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => usersService.getUsers(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUser(id: string) {
  return useQuery({
    queryKey: ['admin', 'users', id],
    queryFn: () => usersService.getUser(id),
    enabled: !!id,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateUserInput) => usersService.createUser(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateUserInput) => usersService.updateUser(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}

export function useUpdateUserLocations() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateUserLocationsInput) =>
      usersService.updateUserLocations(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}

export function useSendPasswordReset() {
  return useMutation({
    mutationFn: (email: string) => usersService.sendPasswordReset(email),
  });
}

// ============================================================================
// DASHBOARD SETTINGS
// ============================================================================

export function useDashboardSettings(location_id: string) {
  return useQuery({
    queryKey: ['admin', 'dashboard-settings', location_id],
    queryFn: () => dashboardSettingsService.getDashboardSettings(location_id),
    enabled: !!location_id,
  });
}

export function useDashboardSettingsForLocations(location_ids: string[]) {
  return useQuery({
    queryKey: ['admin', 'dashboard-settings', 'bulk', location_ids],
    queryFn: () =>
      dashboardSettingsService.getDashboardSettingsForLocations(location_ids),
    enabled: location_ids.length > 0,
  });
}

export function useUpdateDashboardSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateDashboardSettingsInput) =>
      dashboardSettingsService.updateDashboardSettings(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['admin', 'dashboard-settings'],
      });
      queryClient.invalidateQueries({
        queryKey: ['dashboard'], // Invalidate dashboard queries
      });
    },
  });
}

// ============================================================================
// LOCATION-MANUFACTURER SETTINGS
// ============================================================================

export function useLocationManufacturerSettings(location_id: string) {
  return useQuery({
    queryKey: ['admin', 'location-manufacturer-settings', location_id],
    queryFn: () =>
      locationManufacturerSettingsService.getLocationManufacturerSettings(
        location_id
      ),
    enabled: !!location_id,
  });
}

export function useLocationManufacturerSetting(
  location_id: string,
  manufacturer_id: string
) {
  return useQuery({
    queryKey: [
      'admin',
      'location-manufacturer-settings',
      location_id,
      manufacturer_id,
    ],
    queryFn: () =>
      locationManufacturerSettingsService.getLocationManufacturerSetting(
        location_id,
        manufacturer_id
      ),
    enabled: !!location_id && !!manufacturer_id,
  });
}

export function useCreateLocationManufacturerSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateLocationManufacturerSettingsInput) =>
      locationManufacturerSettingsService.createLocationManufacturerSettings(
        input
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          'admin',
          'location-manufacturer-settings',
          variables.location_id,
        ],
      });
      queryClient.invalidateQueries({ queryKey: ['weekly-ordering'] });
    },
  });
}

export function useUpdateLocationManufacturerSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateLocationManufacturerSettingsInput) =>
      locationManufacturerSettingsService.updateLocationManufacturerSettings(
        input
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['admin', 'location-manufacturer-settings'],
      });
      queryClient.invalidateQueries({ queryKey: ['weekly-ordering'] });
    },
  });
}

export function useDeleteLocationManufacturerSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      locationManufacturerSettingsService.deleteLocationManufacturerSettings(
        id
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['admin', 'location-manufacturer-settings'],
      });
      queryClient.invalidateQueries({ queryKey: ['weekly-ordering'] });
    },
  });
}

// ============================================================================
// NOTIFICATION SETTINGS
// ============================================================================

export function useNotificationSettings() {
  return useQuery({
    queryKey: ['admin', 'notification-settings'],
    queryFn: () => notificationSettingsService.getNotificationSettings(),
  });
}

export function useNotificationSettingsByLocation(location_id: string | null) {
  return useQuery({
    queryKey: ['admin', 'notification-settings', 'by-location', location_id],
    queryFn: () =>
      notificationSettingsService.getNotificationSettingsByLocation(
        location_id
      ),
  });
}

export function useCreateNotificationSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateNotificationSettingsInput) =>
      notificationSettingsService.createNotificationSettings(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['admin', 'notification-settings'],
      });
    },
  });
}

export function useUpdateNotificationSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateNotificationSettingsInput) =>
      notificationSettingsService.updateNotificationSettings(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['admin', 'notification-settings'],
      });
    },
  });
}

export function useDeleteNotificationSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      notificationSettingsService.deleteNotificationSettings(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['admin', 'notification-settings'],
      });
    },
  });
}

// ============================================================================
// APP SETTINGS (FEATURE FLAGS & REPORTS)
// ============================================================================

export function useFeatureFlags() {
  return useQuery({
    queryKey: ['admin', 'feature-flags'],
    queryFn: () => settingsService.getFeatureFlags(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useReportSettings() {
  return useQuery({
    queryKey: ['admin', 'report-settings'],
    queryFn: () => settingsService.getReportSettings(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useAppSettings() {
  return useQuery({
    queryKey: ['admin', 'app-settings'],
    queryFn: () => settingsService.getAllSettings(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useToggleFeatureFlag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      settingsService.toggleFeatureFlag(key, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flags'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'app-settings'] });
    },
  });
}

export function useToggleReportSetting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, visible }: { key: string; visible: boolean }) =>
      settingsService.toggleReportSetting(key, visible),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'report-settings'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'app-settings'] });
    },
  });
}

export function useCreateAppSetting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      key,
      value,
      description,
    }: {
      key: string;
      value: string;
      description?: string;
    }) => settingsService.createSetting(key, value, description),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'app-settings'] });
    },
  });
}

export function useUpdateAppSetting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) =>
      settingsService.updateSetting(key, value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'app-settings'] });
    },
  });
}

export function useDeleteAppSetting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => settingsService.deleteSetting(key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'app-settings'] });
    },
  });
}
